import { Fragment, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, FolderOpen, Loader2, Pencil, Plus, Power } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useAdminCategories,
  useCreateCategory,
  useUpdateCategory,
  useDeactivateCategory,
  useAdminRates,
  useAdminNiches,
  useAdminTeams,
  type OutreachCategory,
  type PayoutFormula,
} from '@/hooks/useAdmin';
import { Pill } from '@/components/ui/pill';
import { Tag } from '@/components/ui/tag';
import { Combobox } from '@/components/ui/combobox';
import {
  DataTable,
  DataTableExpandedCell,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableRow,
} from '@/components/ui/data-table';
import { PageHeader } from '@/components/ui/page-header';
import { formatMoney } from '@/lib/money';
import type { OutreachNiche, OutreachRate } from '@/api/types';
import { rateTypeLabel } from '@/lib/labels';

/** How an invite of this creator type is paid, in words. */
const FORMULAS: { value: PayoutFormula; label: string; description: string }[] = [
  {
    value: 'FULL_RATE_CARD',
    label: 'Full rate card',
    description:
      "Pays when logged, when the creator joins, and per approved post (up to the post limit), plus any niche bonus for the invite's first niche and a follower bonus once the creator posts. Discord invites earn the logged amount only after the creator joins the server. The default for new creator types.",
  },
  {
    value: 'SIMPLE_LOG_BONUS',
    label: 'Logged amount only',
    description:
      "Pays one flat amount when the invite is logged (the creator type's “When logged” rate). No join, post, niche or follower bonuses.",
  },
];
const formulaLabel = (f: string): string => FORMULAS.find((x) => x.value === f)?.label ?? 'Unknown formula';

interface FormState {
  code: string;
  displayName: string;
  payoutFormula: PayoutFormula;
}

function emptyForm(): FormState {
  return { code: '', displayName: '', payoutFormula: 'FULL_RATE_CARD' };
}

export function AdminCategories(): JSX.Element {
  const query = useAdminCategories();
  const rates = useAdminRates();
  const niches = useAdminNiches();
  const teams = useAdminTeams();
  const createMut = useCreateCategory();
  const updateMut = useUpdateCategory();
  const deactivateMut = useDeactivateCategory();

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<FormState>(emptyForm);
  const [editing, setEditing] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<FormState>(emptyForm);
  const [expandedCode, setExpandedCode] = useState<string | null>(null);

  // Per-category roll-ups powering the expandable detail panel.
  const ratesByCategory = useMemo(() => {
    const out = new Map<string, OutreachRate[]>();
    for (const r of rates.data ?? []) {
      if (!r.isActive) continue;
      const list = out.get(r.category) ?? [];
      list.push(r);
      out.set(r.category, list);
    }
    return out;
  }, [rates.data]);

  const nichesByCategory = useMemo(() => {
    const out = new Map<string, OutreachNiche[]>();
    const globals: OutreachNiche[] = [];
    for (const n of niches.data ?? []) {
      // Hidden niches aren't offered to anyone any more.
      if (n.isHidden) continue;
      if (n.categoryCodes.length === 0) {
        globals.push(n);
        continue;
      }
      for (const c of n.categoryCodes) {
        const list = out.get(c) ?? [];
        list.push(n);
        out.set(c, list);
      }
    }
    return { out, globals };
  }, [niches.data]);

  const nicheNameByCode = useMemo(
    () => new Map((niches.data ?? []).map((n) => [n.code, n.displayName])),
    [niches.data],
  );

  const teamsByCategory = useMemo(() => {
    const out = new Map<string, Array<{ code: string; displayName: string }>>();
    for (const t of teams.data ?? []) {
      for (const c of t.inviteCategories ?? []) {
        const list = out.get(c) ?? [];
        list.push({ code: t.code, displayName: t.displayName });
        out.set(c, list);
      }
    }
    return out;
  }, [teams.data]);

  if (query.isLoading) {
    return (
      <div className="flex items-center text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading
      </div>
    );
  }
  if (query.error || !query.data) {
    return <p className="text-sm text-destructive">{(query.error as Error)?.message ?? 'Error'}</p>;
  }

  function startEdit(c: OutreachCategory): void {
    setEditing(c.code);
    setEditForm({ code: c.code, displayName: c.displayName, payoutFormula: c.payoutFormula });
  }

  function submitCreate(): void {
    if (!createForm.code || !createForm.displayName) {
      toast.error('Add a code and a name first.');
      return;
    }
    createMut.mutate(
      {
        code: createForm.code,
        displayName: createForm.displayName,
        payoutFormula: createForm.payoutFormula,
      },
      {
        onSuccess: () => {
          toast.success(`Added ${createForm.displayName}`);
          setCreateOpen(false);
          setCreateForm(emptyForm());
        },
        onError: (err: Error) => toast.error(`Couldn't add the creator type: ${err.message}`),
      },
    );
  }

  function submitEdit(code: string): void {
    updateMut.mutate(
      {
        code,
        body: { displayName: editForm.displayName, payoutFormula: editForm.payoutFormula },
      },
      {
        onSuccess: () => {
          toast.success(`Saved ${editForm.displayName}`);
          setEditing(null);
        },
        onError: (err: Error) => toast.error(`Couldn't save the creator type: ${err.message}`),
      },
    );
  }

  function deactivate(c: OutreachCategory): void {
    if (!c.isActive) return;
    deactivateMut.mutate(c.code, {
      onSuccess: () => toast.success(`${c.displayName} turned off`),
      onError: (err: Error) => toast.error(`Couldn't turn off ${c.displayName}: ${err.message}`),
    });
  }

  function reactivate(c: OutreachCategory): void {
    if (c.isActive) return;
    updateMut.mutate(
      { code: c.code, body: { isActive: true } },
      {
        onSuccess: () => toast.success(`${c.displayName} turned back on`),
        onError: (err: Error) => toast.error(`Couldn't turn on ${c.displayName}: ${err.message}`),
      },
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Creator types"
        description="The kind of creator an invite is for (for example Clipper or Influencer). Each creator type has its own rates and follower bonuses and a payout formula that decides which of them apply. Teams choose which creator types their outreachers can log. The two formulas below are the only ones available; a new formula needs an engineering change."
        actions={
          <Button onClick={() => setCreateOpen((v) => !v)}>
            <Plus className="mr-1 h-4 w-4" /> {createOpen ? 'Cancel' : 'New creator type'}
          </Button>
        }
      />

      {createOpen && (
        <Card>
          <CardHeader>
            <CardTitle>New creator type</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <CategoryFormFields form={createForm} onChange={setCreateForm} showCode />
            <Button onClick={submitCreate} disabled={createMut.isPending}>
              {createMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add creator type
            </Button>
          </CardContent>
        </Card>
      )}

      <DataTable>
        <DataTableHeader>
          <tr>
            <DataTableHeaderCell narrow className="hidden sm:table-cell" />
            <DataTableHeaderCell>Creator type</DataTableHeaderCell>
            <DataTableHeaderCell>Payout formula</DataTableHeaderCell>
            <DataTableHeaderCell>Status</DataTableHeaderCell>
            <DataTableHeaderCell className="text-right">Actions</DataTableHeaderCell>
          </tr>
        </DataTableHeader>
        <tbody>
          {query.data.map((c) => {
            const isEditing = editing === c.code;
            const isExpanded = expandedCode === c.code;
            const linkedRates = ratesByCategory.get(c.code) ?? [];
            const scopedNiches = nichesByCategory.out.get(c.code) ?? [];
            const globalNiches = nichesByCategory.globals;
            const linkedTeams = teamsByCategory.get(c.code) ?? [];
            return (
              <Fragment key={c.code}>
                <DataTableRow
                  state={isExpanded ? 'expanded' : 'default'}
                  className="align-top"
                >
                  <td className="hidden w-6 py-2 align-middle sm:table-cell">
                    <button
                      type="button"
                      onClick={() => setExpandedCode(isExpanded ? null : c.code)}
                      className="-ml-1 flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                      aria-label={`${isExpanded ? 'Hide' : 'Show'} details for ${c.displayName}`}
                      aria-expanded={isExpanded}
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-3.5 w-3.5" strokeWidth={2} />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5" strokeWidth={2} />
                      )}
                    </button>
                  </td>
                  <td className="py-2 font-medium">
                    <span className="inline-flex items-center gap-2">
                      <FolderOpen
                        className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                        strokeWidth={2}
                        aria-hidden
                      />
                      {c.displayName}
                    </span>
                  </td>
                  <td className="py-2 text-xs" data-label="Payout formula">
                    <span title={FORMULAS.find((f) => f.value === c.payoutFormula)?.description}>
                      {formulaLabel(c.payoutFormula)}
                    </span>
                  </td>
                  <td className="py-2" data-label="Status">
                    <Pill tone={c.isActive ? 'paid' : 'muted'}>
                      {c.isActive ? 'Active' : 'Inactive'}
                    </Pill>
                  </td>
                  <td className="actions py-2 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => startEdit(c)}
                      aria-label={`Edit ${c.displayName}`}
                      title="Edit name and payout formula"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {c.isActive ? (
                      <ConfirmDialog
                        title={`Turn off ${c.displayName}?`}
                        description={
                          <>
                            It stops being offered when setting up teams. Existing invites and
                            payouts are unaffected. Turn it back on any time; its name, formula
                            and rates are kept.
                          </>
                        }
                        tone="destructive"
                        confirmLabel="Turn off creator type"
                        pending={deactivateMut.isPending}
                        onConfirm={() => deactivate(c)}
                        trigger={
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Turn off"
                            aria-label={`Turn off ${c.displayName}`}
                          >
                            <Power className="text-destructive-strong h-4 w-4" />
                          </Button>
                        }
                      />
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => reactivate(c)}
                        disabled={updateMut.isPending}
                        title="Turn back on"
                        aria-label={`Turn ${c.displayName} back on`}
                      >
                        <Power className="text-success-strong h-4 w-4" />
                      </Button>
                    )}
                  </td>
                </DataTableRow>
                {isEditing && (
                  <DataTableRow state="expanded">
                    <td className="hidden sm:table-cell"></td>
                    <td colSpan={4} className="p-4">
                      <div className="space-y-3">
                        <CategoryFormFields form={editForm} onChange={setEditForm} />
                        <div className="flex gap-2">
                          <Button onClick={() => submitEdit(c.code)} disabled={updateMut.isPending}>
                            {updateMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Save
                          </Button>
                          <Button variant="ghost" onClick={() => setEditing(null)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </td>
                  </DataTableRow>
                )}
                {isExpanded && !isEditing && (
                  <DataTableRow state="expanded">
                    <td className="hidden sm:table-cell"></td>
                    <DataTableExpandedCell colSpan={4}>
                      <CategoryDetailPanel
                        category={c}
                        linkedRates={linkedRates}
                        scopedNiches={scopedNiches}
                        globalNiches={globalNiches}
                        linkedTeams={linkedTeams}
                        nicheNameByCode={nicheNameByCode}
                      />
                    </DataTableExpandedCell>
                  </DataTableRow>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </DataTable>
    </div>
  );
}

function CategoryDetailPanel({
  category,
  linkedRates,
  scopedNiches,
  globalNiches,
  linkedTeams,
  nicheNameByCode,
}: {
  nicheNameByCode: Map<string, string>;
  category: OutreachCategory;
  linkedRates: OutreachRate[];
  scopedNiches: OutreachNiche[];
  globalNiches: OutreachNiche[];
  linkedTeams: Array<{ code: string; displayName: string }>;
}): JSX.Element {
  return (
    <div className="grid gap-5 px-4 py-4 text-sm md:grid-cols-3">
      <DetailSection title="Rates" hint={`${linkedRates.length} active`}>
        {linkedRates.length === 0 ? (
          <EmptyHint>
            No active rates for {category.displayName}, so its invites earn nothing yet.
          </EmptyHint>
        ) : (
          <ul className="space-y-1">
            {linkedRates.map((r) => (
              <li
                key={r.id}
                className="flex items-baseline justify-between gap-3 rounded-md border border-border bg-card px-2.5 py-1"
              >
                <div className="flex flex-wrap items-baseline gap-1.5">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {rateTypeLabel(r.rateType)}
                  </span>
                  {r.niche && (
                    <span className="text-[11px] text-foreground/80">
                      {nicheNameByCode.get(r.niche) ?? r.niche} bonus
                    </span>
                  )}
                </div>
                <span className="font-semibold tabular-nums text-primary">
                  ${formatMoney(r.amount)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </DetailSection>

      <DetailSection title="Niches" hint={`${scopedNiches.length} just for this type · ${globalNiches.length} for every type`}>
        {scopedNiches.length === 0 && globalNiches.length === 0 ? (
          <EmptyHint>No niches available for this creator type.</EmptyHint>
        ) : (
          <div className="space-y-2">
            {scopedNiches.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {scopedNiches.map((n) => (
                  <Tag key={n.code}>{n.displayName}</Tag>
                ))}
              </div>
            )}
            {globalNiches.length > 0 && (
              <div className="space-y-1">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Every creator type
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {globalNiches.map((n) => (
                    <Tag key={n.code} tone="neutral">
                      {n.displayName}
                    </Tag>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DetailSection>

      <DetailSection title="Teams" hint={`${linkedTeams.length} team${linkedTeams.length === 1 ? '' : 's'}`}>
        {linkedTeams.length === 0 ? (
          <EmptyHint>
            No team lets its outreachers log {category.displayName} invites yet.
          </EmptyHint>
        ) : (
          <ul className="space-y-1">
            {linkedTeams.map((t) => (
              <li
                key={t.code}
                className="flex items-baseline justify-between gap-3 rounded-md border border-border bg-card px-2.5 py-1"
              >
                <span className="text-xs font-medium text-foreground/90">
                  {t.displayName}
                </span>
              </li>
            ))}
          </ul>
        )}
      </DetailSection>
    </div>
  );
}

function DetailSection({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          {title}
        </span>
        {hint && (
          <span className="text-[10px] text-muted-foreground/80">{hint}</span>
        )}
      </div>
      {children}
    </div>
  );
}

function EmptyHint({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <p className="rounded-md border border-dashed border-border bg-muted/30 p-2 text-xs text-muted-foreground">
      {children}
    </p>
  );
}

interface FormFieldsProps {
  form: FormState;
  onChange: (next: FormState) => void;
  showCode?: boolean;
}

function CategoryFormFields({ form, onChange, showCode }: FormFieldsProps): JSX.Element {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {showCode && (
        <div className="grid gap-1.5">
          <Label htmlFor="category-code">Code</Label>
          <Input
            id="category-code"
            value={form.code}
            onChange={(e) => onChange({ ...form, code: e.target.value.toUpperCase() })}
            placeholder="AFFILIATE"
          />
          <p className="text-xs text-muted-foreground">
            A short ID: A–Z, 0–9 and underscores. It can&apos;t be changed later.
          </p>
        </div>
      )}
      <div className="grid gap-1.5">
        <Label htmlFor="category-display">Name</Label>
        <Input
          id="category-display"
          value={form.displayName}
          onChange={(e) => onChange({ ...form, displayName: e.target.value })}
          placeholder="Affiliate"
        />
      </div>
      <div className="grid gap-1.5 sm:col-span-2">
        <Label htmlFor="category-formula">Payout formula</Label>
        <Combobox
          value={form.payoutFormula}
          onChange={(v) => onChange({ ...form, payoutFormula: v as PayoutFormula })}
          options={FORMULAS.map((f) => ({ value: f.value, label: f.label }))}
          placeholder="Pick a formula"
        />
        <p className="text-xs text-muted-foreground">
          {FORMULAS.find((f) => f.value === form.payoutFormula)?.description}
        </p>
      </div>
    </div>
  );
}
